import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getUserGroups } from '../services/groupService';
import type { Group, GroupContextType } from '../types';
import { useAuth } from './AuthContext';

// グループコンテキスト作成
const GroupContext = createContext<GroupContextType | null>(null);

// グループプロバイダーコンポーネント
export const GroupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [groups, setGroups] = useState<Group[]>([]);
  const [currentGroup, setCurrentGroup] = useState<Group | null>(null);
  const [loading, setLoading] = useState(true);

  // グループ一覧を取得
  const refreshGroups = useCallback(async () => {
    if (!user) {
      setGroups([]);
      setCurrentGroup(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const userGroups = await getUserGroups(user.uid);
      setGroups(userGroups);

      // 現在選択中のグループが一覧にない場合はリセット
      if (currentGroup && !userGroups.find((g) => g.id === currentGroup.id)) {
        setCurrentGroup(null);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [user, currentGroup]);

  // ユーザーが変わったらグループを再取得
  useEffect(() => {
    refreshGroups();
  }, [refreshGroups]);

  const value: GroupContextType = {
    groups,
    currentGroup,
    loading,
    setCurrentGroup,
    refreshGroups,
  };

  return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>;
};

// カスタムフック
export const useGroups = (): GroupContextType => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error('useGroups must be used within a GroupProvider');
  }
  return context;
};

export default GroupContext;
